import { create } from "zustand";

type LegacyBrandDashboardPeriod = "1w" | "1m" | "3m" | "6m";
export type BrandDashboardPeriod = "7d" | "14d" | "30d" | "90d" | "180d";

interface UserPreferencesStore {
  userId: string | null;
  defaultBrandDashboardPeriod: BrandDashboardPeriod;
  initializeUser: (userId: string) => void;
  setDefaultBrandDashboardPeriod: (period: BrandDashboardPeriod) => void;
}

type StoredUserPreferencesState = {
  userId: string;
  defaultBrandDashboardPeriod: BrandDashboardPeriod;
};

const DEFAULT_BRAND_DASHBOARD_PERIOD: BrandDashboardPeriod = "7d";
const USER_PREFERENCES_LEGACY_KEY = "user-preferences-store";
const USER_PREFERENCES_STORAGE_PREFIX = "user-preferences-store-";

const getStorageName = (userId: string) => `${USER_PREFERENCES_STORAGE_PREFIX}${userId}`;

const LEGACY_PERIOD_MIGRATION: Record<LegacyBrandDashboardPeriod, BrandDashboardPeriod> = {
  "1w": "7d",
  "1m": "30d",
  "3m": "90d",
  "6m": "180d",
};

const isBrandDashboardPeriod = (value: unknown): value is BrandDashboardPeriod =>
  value === "7d" || value === "14d" || value === "30d" || value === "90d" || value === "180d";

const normalizeBrandDashboardPeriod = (value: unknown): BrandDashboardPeriod | null => {
  if (isBrandDashboardPeriod(value)) {
    return value;
  }

  if (value === "1w" || value === "1m" || value === "3m" || value === "6m") {
    return LEGACY_PERIOD_MIGRATION[value];
  }

  return null;
};

const readStoredState = (userId: string): StoredUserPreferencesState | null => {
  try {
    const raw = localStorage.getItem(getStorageName(userId));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredUserPreferencesState>;
    if (typeof parsed.userId !== "string" || parsed.userId !== userId) {
      return null;
    }

    const normalizedPeriod = normalizeBrandDashboardPeriod(parsed.defaultBrandDashboardPeriod);
    if (!normalizedPeriod) {
      return null;
    }

    return {
      userId,
      defaultBrandDashboardPeriod: normalizedPeriod,
    };
  } catch (error) {
    console.error("Failed to read stored user preferences:", error);
    return null;
  }
};

const writeStoredState = (state: StoredUserPreferencesState): void => {
  try {
    localStorage.setItem(getStorageName(state.userId), JSON.stringify(state));
  } catch (error) {
    console.error("Failed to persist user preferences:", error);
  }
};

const cleanupOtherUserStores = (currentStorageName: string): void => {
  try {
    const keysToRemove: string[] = [];

    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (key?.startsWith(USER_PREFERENCES_STORAGE_PREFIX) && key !== currentStorageName) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error("Failed to cleanup stored user preferences:", error);
  }
};

const migrateLegacyUserPreferences = (userId: string): void => {
  try {
    const storageName = getStorageName(userId);
    if (localStorage.getItem(storageName)) {
      localStorage.removeItem(USER_PREFERENCES_LEGACY_KEY);
      return;
    }

    const rawLegacyState = localStorage.getItem(USER_PREFERENCES_LEGACY_KEY);
    if (!rawLegacyState) {
      return;
    }

    const parsedLegacyState = JSON.parse(rawLegacyState) as {
      state?: {
        defaultBrandDashboardPeriod?: unknown;
      };
    };

    const normalizedPeriod = normalizeBrandDashboardPeriod(parsedLegacyState.state?.defaultBrandDashboardPeriod);
    if (!normalizedPeriod) {
      localStorage.removeItem(USER_PREFERENCES_LEGACY_KEY);
      return;
    }

    writeStoredState({
      userId,
      defaultBrandDashboardPeriod: normalizedPeriod,
    });
    localStorage.removeItem(USER_PREFERENCES_LEGACY_KEY);
  } catch (error) {
    console.error("Failed to migrate legacy user preferences:", error);
  }
};

export const useUserPreferencesStore = create<UserPreferencesStore>((set, get) => ({
  userId: null,
  defaultBrandDashboardPeriod: DEFAULT_BRAND_DASHBOARD_PERIOD,
  initializeUser: (userId: string) => {
    const normalizedUserId = userId.trim();
    if (!normalizedUserId) {
      return;
    }

    const storageName = getStorageName(normalizedUserId);
    cleanupOtherUserStores(storageName);
    migrateLegacyUserPreferences(normalizedUserId);

    const storedState = readStoredState(normalizedUserId);
    set({
      userId: normalizedUserId,
      defaultBrandDashboardPeriod: storedState?.defaultBrandDashboardPeriod ?? DEFAULT_BRAND_DASHBOARD_PERIOD,
    });
  },
  setDefaultBrandDashboardPeriod: (period: BrandDashboardPeriod) => {
    const userId = get().userId;

    set({ defaultBrandDashboardPeriod: period });

    if (!userId) {
      return;
    }

    writeStoredState({
      userId,
      defaultBrandDashboardPeriod: period,
    });
  },
}));
